import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClients, useCreateClient } from "@/hooks/useClients";

/**
 * O cliente do projeto novo (#1779): escolhe um dos clientes do tenant ou cria um na hora. É pelo
 * cliente que o banco do cliente é escolhido, e por isso o projeto não nasce sem ele.
 */
export function ClientPicker({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: string;
  onChange: (clientId: string) => void;
}) {
  const { t } = useTranslation();
  const { data: clients = [] } = useClients();
  const createClient = useCreateClient();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function add() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      const client = await createClient.mutateAsync(name);
      onChange(client.id);
      setAdding(false);
      setNewName("");
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(
        status === 409
          ? t("A client with this name already exists.")
          : t("Could not create the client."),
      );
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-client`}>
        {t("Client")} <span className="text-destructive">*</span>
      </Label>
      {adding ? (
        <div className="flex gap-2">
          <Input
            id={`${idPrefix}-client-name`}
            aria-label={t("New client name")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button
            type="button"
            onClick={add}
            disabled={!newName.trim() || createClient.isPending}
          >
            {t("Add client")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setError(null);
            }}
          >
            {t("Choose an existing client")}
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger id={`${idPrefix}-client`} aria-label={t("Client")}>
              <SelectValue placeholder={t("Select the client")} />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={() => setAdding(true)}>
            {t("New client")}
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
